import type { LearnFamilyId, ModuleTemplate } from '../types/module';
import {
  lastActivityAt,
  type LearnModuleId,
  type LearnModuleSnapshot,
} from '../learn';
import { MODULE_TEMPLATES } from './registry';
import { TRIAD_GROUPS } from '../../utils/trainingModules';

export interface LearnFamily {
  id: LearnFamilyId;
  title: string;
  outcome: string;
  caption: string;
  guidance: string;
}

export const LEARN_FAMILIES: LearnFamily[] = [
  {
    id: 'tabs',
    title: 'Play a tab',
    outcome: 'Follow the next notes directly on your guitar.',
    caption: 'Import a passage or try an original exercise',
    guidance:
      'Start at half speed, follow the bright target, and use the next-note ring to prepare your fingers. Pause or step through a tricky moment. Camera only; no scoring.',
  },
  {
    id: 'pentatonic',
    title: 'Pentatonic scales',
    outcome: 'Find your way around a solo, one shape at a time.',
    caption: 'All five positions',
    guidance:
      'Press play, set the tempo and how many bars to stay on each position, and JamCam walks all five positions in a loop. Find the roots as you go. Choose major or minor and your key on the stage.',
  },
  {
    id: 'blues',
    title: 'Blues scales',
    outcome: 'Add the blue note to familiar pentatonic shapes.',
    caption: 'Build on pentatonic',
    guidance:
      'Explore the extra note that gives the blues scale its color. Press play to walk the positions on a timed loop. Choose major or minor and your key on the stage.',
  },
  {
    id: 'diatonic',
    title: 'Major & minor scales',
    outcome: 'Explore the seven notes that make up a key.',
    caption: 'Explore the whole neck',
    guidance:
      'Press play to walk all seven positions on a timed loop, staying as many bars on each as you like. Choose major or minor and your key on the stage; try another key once the shapes feel familiar.',
  },
  {
    id: 'triads',
    title: 'Triads',
    outcome: 'Find compact, three-note chords across the neck.',
    caption: 'Choose a three-string set',
    guidance:
      'Choose a chord quality and string set, then press play to cycle root position and both inversions on a timed loop. Start slowly and listen to each chord tone.',
  },
  {
    id: 'progressions',
    title: 'Chord progressions',
    outcome: 'Practice chord changes with a steady pulse.',
    caption: 'Build a progression and play',
    guidance:
      'Choose a familiar progression, adjust the key and voicings, and start at a comfortable tempo. Follow the playhead as each chord lights up on your guitar.',
  },
  {
    id: 'soloing',
    title: 'Guided soloing',
    outcome: 'Make your note choices follow the harmony.',
    caption: 'Apply scales to changing chords',
    guidance:
      'Follow chord-tone guidance over a progression. Start with a few notes, listen to the harmony, and use the riff prompts to explore new phrases.',
  },
];

export const AVAILABLE_LEARN_MODULES = MODULE_TEMPLATES.filter(
  (module) => module.available
);

export function getFamilyModules(family: LearnFamilyId): ModuleTemplate[] {
  return AVAILABLE_LEARN_MODULES.filter(
    (module) => module.learnFamily === family
  ).sort(
    (a, b) =>
      (a.learnOrder ?? a.defaults.triadGroupIndex ?? 0) -
      (b.learnOrder ?? b.defaults.triadGroupIndex ?? 0)
  );
}

export function learnStepLabel(module: ModuleTemplate): string {
  if (module.learnFamily === 'triads') {
    return `String set ${(module.defaults.triadGroupIndex ?? 0) + 1} · ${
      TRIAD_GROUPS[module.defaults.triadGroupIndex ?? 0].label
    }`;
  }
  if (['pentatonic', 'blues', 'diatonic'].includes(module.learnFamily ?? '')) {
    return 'All positions';
  }
  return module.title;
}

export function practiceHistory(
  snapshot?: LearnModuleSnapshot
): string | undefined {
  if (!snapshot?.lastPracticedAt) return undefined;
  const date = new Date(snapshot.lastPracticedAt).toLocaleDateString(
    undefined,
    { month: 'short', day: 'numeric' }
  );
  return snapshot.completedRuns > 0
    ? `${snapshot.completedRuns} completed ${
        snapshot.completedRuns === 1 ? 'run' : 'runs'
      } · Practiced ${date}`
    : `Practiced ${date}`;
}

/** Continue only available, accessible activities; otherwise offer the free pentatonic scale. */
export function getFeaturedModule(
  snapshots: Partial<Record<LearnModuleId, LearnModuleSnapshot>>,
  canUse: (module: ModuleTemplate) => boolean
): ModuleTemplate {
  const activity = (module: ModuleTemplate) =>
    lastActivityAt(snapshots[module.id as LearnModuleId]);
  const recent = AVAILABLE_LEARN_MODULES.filter(
    (module) => canUse(module) && activity(module) > 0
  ).sort((a, b) => activity(b) - activity(a));
  return recent[0] ?? getFamilyModules('pentatonic')[0];
}

/** How many of a family's activities have been practiced at least once. */
export function familyProgress(
  family: LearnFamilyId,
  snapshots: Partial<Record<LearnModuleId, LearnModuleSnapshot>>
): { practiced: number; total: number } {
  const modules = getFamilyModules(family);
  return {
    practiced: modules.filter(
      (module) => snapshots[module.id as LearnModuleId]?.lastPracticedAt
    ).length,
    total: modules.length,
  };
}

/** Short progress label, or undefined before the first practice. */
export function familyProgressLabel({
  practiced,
  total,
}: {
  practiced: number;
  total: number;
}): string | undefined {
  if (!practiced) return undefined;
  return total > 1 ? `${practiced} of ${total} practiced` : 'Practiced';
}
